import { Close } from "@carbon/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import PageLoader from "../../../../components/PageLoader";
import FallbackComponent from "../../../../components/fallbackComponent/FallbackComponent";
import { useOrderContext } from "../../../../contexts/OrderContext";
import { SavedProgressReducerActionTypes } from "../../../../contexts/reducers/savedProgressReducer";
import { useNewOrderQueryData } from "../../../../hooks/useNewOrderQueryData";
import { IOutboundGeneralInfo } from "../../../../models/outboundGeneralInfo";
import OutboundGeneralInfoForm from "../../components/forms/outboundGeneralInfoForm/OutboundGeneralInfoForm";
import BaseContent from "../BaseContent";

interface GeneralInfoScreenProps {}

export const emptyOutboundGeneralInfoFormData: IOutboundGeneralInfo = {
  deliveryNumber: "",
  location: "",
  locationId: "",
  recipient: "",
  sender: "",
  carrier: "",
  carrierTenantId: "",
};

const isKeyOfFormData = (key: string): key is keyof IOutboundGeneralInfo => {
  return key in emptyOutboundGeneralInfoFormData;
};

const GeneralInfoScreen = (props: GeneralInfoScreenProps) => {
  //#region State and variables
  const { t } = useTranslation();
  const { savedProgress, dispatchSavedProgress, steps } = useOrderContext();
  const { queryData, loading, error } = useNewOrderQueryData();

  const [formData, setFormData] = useState<IOutboundGeneralInfo>(
    emptyOutboundGeneralInfoFormData,
  );
  const [isFormValid, setIsFormValid] = useState(false);
  //#endregion

  const loadSavedProgress = () => {
    if (!savedProgress || !savedProgress.outboundGeneralInfo) return;

    setFormData(savedProgress.outboundGeneralInfo);
  };

  const saveProgress = () => {
    dispatchSavedProgress({
      type: SavedProgressReducerActionTypes.SET_PROP,
      property: "outboundGeneralInfo",
      value: formData,
    });
  };

  const handleLocationChange = (value: string) => {
    if (value === "") {
      // we also need to reset the locationId field
      setFormData({
        ...formData,
        location: "",
        locationId: "",
      });
    } else {
      // we need to set the location field and the locationId field
      setFormData({
        ...formData,
        location: value,
        locationId:
          queryData.locations?.find((location) => location.name === value)
            ?.id || "",
      });
    }
  };

  const onChange = (fieldId: string, value: string) => {
    if (!isKeyOfFormData(fieldId)) {
      console.error(`Invalid fieldId of input: ${fieldId}`);
      return;
    }

    if (fieldId === "location") {
      handleLocationChange(value);
      return;
    }

    if (fieldId === "carrier" && value === "") {
      setFormData({
        ...formData,
        carrier: "",
        carrierTenantId: "",
      });
      return;
    }

    setFormData({
      ...formData,
      [fieldId]: value,
    });
  };

  const validateForm = () => {
    const isValid =
      !!formData.deliveryNumber &&
      !!formData.locationId &&
      !!formData.recipient;
    setIsFormValid(isValid);
  };

  useEffect(() => {
    validateForm();
  }, [formData]);

  useEffect(() => {
    loadSavedProgress();
  }, []);

  if (loading) return <PageLoader text={t("loadingFormData")} />;

  if (error || !queryData.locations)
    return (
      <FallbackComponent
        middleIcon={<Close size={32} />}
        text={`${t("queryError")}. ${t("tryAgainLater")}`}
      />
    );

  return (
    <BaseContent
      screenName={"outboundGeneralInfoScreen"}
      disableNext={!isFormValid}
      proceedButtonText={t("proceedItems")}
      saveProgress={saveProgress}
    >
      <div className="form-component">
        <OutboundGeneralInfoForm
          formData={formData}
          onChange={onChange}
          locations={queryData.locations || []}
        />
      </div>
    </BaseContent>
  );
};

export default GeneralInfoScreen;
